import { useEffect, useRef, useState } from "react";
import { humanServerError, waitForCardRead, klassifikationsOptionen } from "../lib/card-actions";
import { useParams, useNavigate, useLocation, type Location } from "react-router-dom";
import { DetailModal } from "../components/DetailModal";
import { CardDetailActions } from "../components/CardDetailActions";
import { DetailViewShell } from "../components/DetailViewShell";
import { PlausibilityNav, PlausibilityBadge } from "../components/PlausibilityNav";
import { ExpensePdfHighlights } from "../components/ExpensePdfHighlights";
import { ExpenseCompareView, type HighlightReview } from "../components/ExpenseCompareView";
import type { ExpenseFieldName } from "../lib/expenseCellBbox";
import { spatialFromData, leseFrameDrehung } from "../lib/expenseSpatial";
import { api, type Employee, type ExcelImportData } from "../lib/api";
import { ExcelImportDetail } from "../components/ExcelImportDetail";
import { expenseViewFor, cardDocType } from "../lib/docTypeView";
import { type DoppelRegeln } from "../lib/doppel-lohnart";
import { bucketOf } from "../lib/confidence";
import { formatTimeInput } from "../lib/timeFormat";
import { formatDateInput } from "../lib/dateFormat";
import {
  expenseIssues, inheritableDate, canInheritDate,
  normalizeKmValue, fahrtenbuchCrosscheck, fahrtenbuchKmSummen,
} from "../lib/expensePlausibility";

// Eine editierbare Zelle: {value,confidence}-Cell ODER roher String; Fahrtenbuch-
// km-Zeilen (#469) liefern zusätzlich ROHE Zahlen (km/mitfahrer sind int) und
// null (leerer Mitfahrer) — cellVal/cellConf/setCell behandeln alle Varianten.
type Cell = { value: string; confidence?: number } | string | number | null | undefined;

interface ExpenseEntry {
  tag?: { value: string; confidence?: number } | string;
  datum?: { value: string; confidence?: number } | string;
  kz_nr?: { value: string; confidence?: number } | string;
  kunde?: { value: string; confidence?: number } | string;
  zm_abzug?: { value: string; confidence?: number } | string;
  m_verr?: { value: string; confidence?: number } | string;
  ort?: { value: string; confidence?: number } | string;
  abfahrt?: { value: string; confidence?: number } | string;
  rueckkehr?: { value: string; confidence?: number } | string;
  // Fahrtenbuch-km-Zeilen (#469, ocr/fahrtenbuch.py): km/mitfahrer sind int,
  // fahrtstrecke/zweck sind Text; alle nach dem Editieren wieder String.
  km_abfahrt?: Cell;
  km_ankunft?: Cell;
  gefahrene_km?: Cell;
  mitfahrer?: Cell;
  fahrtstrecke?: Cell;
  zweck?: Cell;
}

interface Expense {
  id: string;
  client_id: string;
  employee_id: string | null;
  month: string;
  state: string;
  /** 'excel_import' (T13/T14, Migration 041): 3g-Excel-Report OHNE PDF —
   *  Detail rendert ExcelImportDetail statt Formular + PDF-Panel. */
  kind: "expense" | "unknown" | "excel_import";
  pdf_path: string;
  pdf_orig_filename: string;
  pdf_bytes?: number;
  data: {
    entries?: ExpenseEntry[];
    header?: {
      month_raw?: string;
      dienstnehmer_raw?: string;
      employee_match_score?: number;
      suggestions?: Array<{ id: string; name: string; score: number }>;
    };
    conflict?: any;
    merge_proposal?: any;
    ocr_error?: string;
    meta?: { spatial?: any };
    highlight_review?: HighlightReview;
  } & Partial<ExcelImportData>;
  created_at: string;
  updated_at: string;
}

// AUSSCHLIESSLICH 3 Felder (2026-06-10, Thomas): Datum + Abfahrt + Rückkehr.
// Alle anderen Formularspalten (Tag, Kd Nr, Kunde, Z/M, M Verr., Ort) werden
// bewusst NICHT digitalisiert — die OCR maskiert sie bereits im Scan
// (Datenminimierung + Geschwindigkeit). Aus den 3 Feldern wird die Stunden-
// Summe berechnet (jede angefangene Stunde = ganze Stunde).
const FIELDS: Array<{ key: keyof ExpenseEntry; label: string; placeholder: string }> = [
  { key: "datum", label: "Datum", placeholder: "TT.MM.JJJJ" },
  { key: "abfahrt", label: "Abfahrt Zeit", placeholder: "HH:MM" },
  { key: "rueckkehr", label: "Rückkehr Zeit", placeholder: "HH:MM" },
];

// Felder, die als PDF-Overlay-Zellen gerendert werden (= die sichtbaren Tabellenspalten).
const OVERLAY_FIELDS = FIELDS.map((f) => f.key) as ExpenseFieldName[];

// Fahrtenbuch km-Zeilen (#469): eigenes Spaltenset, IN-FRAME statt Diäten-3-Feld.
// Reihenfolge/Labels aus ocr/config/3g/fahrtenbuch.json (spalten[]). km-Felder
// sind Ganzzahlen (Tacho/Kilometer), fahrtstrecke/zweck Freitext.
const FAHRTENBUCH_FIELDS: Array<{ key: keyof ExpenseEntry; label: string; placeholder: string }> = [
  { key: "datum", label: "Datum", placeholder: "TT.MM.JJ" },
  { key: "km_abfahrt", label: "km Abfahrt", placeholder: "z.B. 12345" },
  { key: "km_ankunft", label: "km Ankunft", placeholder: "z.B. 12420" },
  { key: "gefahrene_km", label: "gefahrene km", placeholder: "z.B. 75" },
  { key: "mitfahrer", label: "Mitfahrer", placeholder: "—" },
  { key: "fahrtstrecke", label: "Fahrtstrecke", placeholder: "Ausgangspunkt – Zielpunkt" },
  { key: "zweck", label: "Zweck", placeholder: "Zweck der Fahrt" },
];

// Fixes Zeilen-Grid wie bei der Stundenliste (dort 31 Tage): das Diäten-
// Formular hat IMMER 22 Zeilen (korrigiert 2026-06-10, Thomas — deckt sich
// mit dem Template: 23 row_boundaries = 22 Datenzeilen). Zeilen können leer
// sein, aber nie gelöscht oder hinzugefügt werden. OCR liefert mal 0, mal 3
// Entries — die View padded immer aufs fixe Grid auf; liefert die OCR mehr,
// bleiben die sichtbar (kein Datenverlust, User leert sie).
const EXPENSE_FIXED_ROWS = 22;

// Tagessatz-Panel ausgeblendet, solange die Berechnung deaktiviert ist
// (2026-06-10) — Code bleibt für die mögliche Wiederaufnahme erhalten.
const SHOW_EXPENSE_CALC_PANEL = false;

function padEntries(list: ExpenseEntry[]): ExpenseEntry[] {
  const out = [...list];
  while (out.length < EXPENSE_FIXED_ROWS) out.push({});
  return out;
}

function cellVal(c: ExpenseEntry[keyof ExpenseEntry]): string {
  if (c == null) return "";
  if (typeof c === "string") return c;
  if (typeof c === "number") return Number.isFinite(c) ? String(c) : "";
  return c.value ?? "";
}

function cellConf(c: ExpenseEntry[keyof ExpenseEntry]): number | null {
  if (c == null || typeof c === "string" || typeof c === "number") return null;
  return typeof c.confidence === "number" ? c.confidence : null;
}

async function fetchExpense(id: string): Promise<Expense> {
  const r = await fetch(new URL(`api/expenses/${id}`, document.baseURI).toString(), {
    credentials: "same-origin",
  });
  if (!r.ok) throw new Error(`${r.status}`);
  const body = await r.json();
  return body.expense;
}

/** #506 (alle 4 Typen): Re-OCR-Anstoß — Spiegel von timesheets/start-ocr
 *  (Hub-Route expenses/:id/start-ocr existiert seit 2026-06-10). */
async function startExpenseOcr(id: string): Promise<void> {
  const r = await fetch(new URL(`api/expenses/${id}/start-ocr`, document.baseURI).toString(), {
    method: "POST",
    credentials: "same-origin",
  });
  if (!r.ok) throw new Error(`${r.status}`);
}

async function patchExpense(id: string, patch: { employee_id?: string | null; data?: any }): Promise<Expense> {
  const r = await fetch(new URL(`api/expenses/${id}`, document.baseURI).toString(), {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!r.ok) throw new Error(humanServerError(await r.text()));
  const body = await r.json();
  return body.expense;
}

/** Klick-zu-Kopieren Karten-ID — gleiche UX wie Timesheet/Kanban. */
function CardIdChip({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — silent */ }
  };
  return (
    <>
      Karten-ID{" "}
      <code
        onClick={onCopy}
        title={copied ? "✓ kopiert" : `Klick zum Kopieren: ${id}`}
        style={{
          fontSize: 11, cursor: "pointer", userSelect: "all",
          padding: "1px 4px", borderRadius: 3,
          background: copied ? "#dcfce7" : "#f1f5f9",
          border: `1px solid ${copied ? "#86efac" : "#e2e8f0"}`,
        }}
      >
        {copied ? "✓ " : ""}{id}
      </code>
    </>
  );
}

async function postHighlightReview(id: string, status: "approved" | "blocked"): Promise<Expense> {
  const r = await fetch(new URL(`api/expenses/${id}/highlight-review`, document.baseURI).toString(), {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ status }),
  });
  if (!r.ok) throw new Error(humanServerError(await r.text()));
  const body = await r.json();
  return body.expense;
}

async function validateExpense(id: string): Promise<void> {
  const r = await fetch(new URL(`api/expenses/${id}/validate`, document.baseURI).toString(), {
    method: "POST",
    credentials: "same-origin",
  });
  if (!r.ok) throw new Error(humanServerError(await r.text()));
}

async function archiveExpense(id: string): Promise<void> {
  const r = await fetch(new URL(`api/expenses/${id}/archive`, document.baseURI).toString(), {
    method: "POST",
    credentials: "same-origin",
  });
  if (!r.ok) throw new Error(humanServerError(await r.text()));
}

export function ExpenseDetailPage({ modal }: { modal?: boolean } = {}) {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const location = useLocation();
  const background = (location.state as { background?: Location } | null)?.background;
  const closePage = () => {
    if (modal && background) nav(background, { replace: false });
    else nav(-1);
  };
  const [expense, setExpense] = useState<Expense | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  // T29: Doppel-Lohnart-Regeln des Mandanten (excel_art → erwartete Lohnarten),
  // damit ExcelImportDetail unvollständige Paare (fehlender Partner) flaggen kann.
  const [doppelLohnartRegeln, setDoppelLohnartRegeln] = useState<DoppelRegeln>({});
  const [pendingEmployeeId, setPendingEmployeeId] = useState<string>("");
  const [entries, setEntries] = useState<ExpenseEntry[]>([]);
  // OCR-Original-Snapshot (beim Laden) — der Vergleichen-Modus zeigt "OCR: x"
  // sobald der editierte Wert davon abweicht (Pendant zur Stundenliste).
  const [originalEntries, setOriginalEntries] = useState<ExpenseEntry[]>([]);
  const [activeCell, setActiveCell] = useState<{ row: number; field: string } | null>(null);
  // 591-Nachtrag (Thomas 24.07., Karte 00e10133): das Fahrtenbuch liess sich
  // OHNE Markierungs-Urteil validieren — Pflicht-Gate wie Stundenliste/
  // Zulagenblatt: freundlicher Hinweis + Scroll zum gelben Feld statt Sperre.
  const [gateNotice, setGateNotice] = useState<string | null>(null);
  const gateBannerRef = useRef<HTMLDivElement | null>(null);
  // Mandanten-Typ-Registry fuer mandanten-richtige Deklarations-Labels
  // (Thomas 24.07.: Bestand-Sprache "Stundenliste/Diäten" war hart kodiert).
  const [mandantTypen, setMandantTypen] = useState<Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>>([]);
  useEffect(() => {
    const cid = expense?.client_id;
    if (!cid) return;
    let cancelled = false;
    api.uploads.docTypes(cid)
      .then((r) => { if (!cancelled) setMandantTypen(r.types); })
      .catch(() => { /* fail-soft: Default-Labels */ });
    return () => { cancelled = true; };
  }, [expense?.client_id]);
  const typLabel = (route: "timesheet" | "expense", fallback: string) => {
    const ziele = mandantTypen.filter((t) => t.routesTo === route);
    return ziele.length === 1 ? ziele[0]!.label : fallback;
  };
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Manuelle Orientierungs-Korrektur (2026-06-10, Thomas): null = Panel zu,
  // sonst die aktuell gewählte CW-Rotation (0/90/180/270) für die Vorschau.
  const [orientFix, setOrientFix] = useState<number | null>(null);
  const [orientBusy, setOrientBusy] = useState(false);
  // Bis die PDF-Vorschau geladen + die Markierungen ausgerichtet sind, sperrt
  // ein Vorhang den Detail-Body: davor sitzen die Rahmen kurz falsch (Bild noch
  // nicht geladen) → man darf noch nichts eintragen. ExpensePdfHighlights meldet
  // onReady, sobald es sicher ist.
  const [previewReady, setPreviewReady] = useState(false);
  // Pfeiltasten-Navigation (Excel-artig, wie Stundenliste).
  const tableRef = useRef<HTMLTableElement | null>(null);
  // Übersicht/Vergleichen — gleiches Pattern wie die Stundenliste (localStorage).
  const [viewMode, setViewMode] = useState<"overview" | "compare">(() => {
    return (localStorage.getItem("sibit_exp_viewmode") as any) === "compare" ? "compare" : "overview";
  });
  const [compareEverActivated, setCompareEverActivated] = useState<boolean>(
    () => localStorage.getItem("sibit_exp_viewmode") === "compare",
  );
  const setViewModePersist = (m: "overview" | "compare") => {
    setViewMode(m);
    if (m === "compare") setCompareEverActivated(true);
    localStorage.setItem("sibit_exp_viewmode", m);
  };

  useEffect(() => {
    if (!id) return;
    fetchExpense(id)
      .then((e) => api.employees.list(e.client_id).then((emps) => [e, emps] as const))
      .then(([e, emps]) => {
        setExpense(e);
        setEmployees(emps);
        setPendingEmployeeId(e.employee_id ?? "");
        setEntries(padEntries(e.data.entries ?? []));
        setOriginalEntries(e.data.entries ?? []);
        setDirty(false);
      })
      .catch((e2) => setErr(e2.message));
  }, [id]);

  // T29: Doppel-Lohnart-Regeln des Mandanten laden (nur für Excel-Import-Karten
  // relevant). Fail-soft: ohne Matrix entfällt nur der „fehlt-ein-Partner"-Check,
  // Einrückung/Badge/Inkonsistenz-Prüfung funktionieren aus den Kartendaten weiter.
  useEffect(() => {
    const cid = expense?.client_id;
    if (!cid || expense?.kind !== "excel_import") return;
    let cancelled = false;
    api.clientRules(cid)
      .then((res) => {
        if (cancelled || !res) return;
        const zulagen = ((res.ruleset as any)?.uebersetzungsmatrix?.zulagen ?? []) as Array<{
          excel?: string;
          lohnart?: number | string;
          lohnarten?: Array<number | string>;
        }>;
        const map: DoppelRegeln = {};
        for (const z of zulagen) {
          if (z.excel && Array.isArray(z.lohnarten) && z.lohnarten.length >= 2) {
            map[z.excel] = { expected: z.lohnarten, primary: z.lohnart };
          }
        }
        setDoppelLohnartRegeln(map);
      })
      .catch(() => { /* fail-soft */ });
    return () => { cancelled = true; };
  }, [expense?.client_id, expense?.kind]);

  // Kartenwechsel → Vorschau gilt wieder als „lädt“, bis das neue Bild da ist.
  useEffect(() => { setPreviewReady(false); }, [id]);

  const setCell = (rowIdx: number, key: keyof ExpenseEntry, value: string) => {
    setEntries((prev) => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], [key]: value };
      return next;
    });
    setDirty(true);
  };

  // Fixes Grid: kein addRow/removeRow — eine Zeile wird „gelöscht“, indem
  // man ihre Felder leert (Zeile bleibt als leere Grid-Zeile bestehen).
  // Explizit "" pro Schlüssel (statt {}): der Server-Merge wertet fehlende
  // Schlüssel als „unverändert lassen“ — nur gesendete Leerwerte löschen
  // (und landen als Correction im per-Zelle-Audit-Trail).
  const clearRow = (idx: number) => {
    setEntries((prev) => prev.map((row, i) => (
      i === idx
        ? Object.fromEntries(Object.keys(row).map((k) => [k, ""])) as ExpenseEntry
        : row
    )));
    setDirty(true);
  };

  // Beim Speichern werden normalisierbare Autocompletion-Felder kanonisch
  // geschrieben (Datum TT.MM.JJJJ, Zeiten HH:MM) — egal ob der User je in
  // das Feld geklickt hat. Nicht-normalisierbare Werte bleiben roh (die
  // Plausibilität markiert sie rot und sperrt das Validieren).
  const canonicalizeEntry = (e: ExpenseEntry): ExpenseEntry => {
    const out: ExpenseEntry = { ...e };
    const fix = (key: keyof ExpenseEntry, fmt: (v: string) => string) => {
      const cur = out[key];
      const v = cellVal(cur);
      if (!v.trim()) return;
      const formatted = fmt(v);
      if (formatted !== v) {
        out[key] = (cur == null || typeof cur === "string" || typeof cur === "number")
          ? formatted
          : { ...cur, value: formatted };
      }
    };
    // #469: Fahrtenbuch normalisiert die km-Ganzzahlen (Tausender-Punkte weg);
    // Freitext (fahrtstrecke/zweck) bleibt roh. 592-R3: datum wird wie bei den
    // Diäten autovervollständigt (Pflichtfeld seit R2 — "020326"→"02.03.2026").
    if (cardDocType(expense?.data) === "fahrtenbuch") {
      (["km_abfahrt", "km_ankunft", "gefahrene_km", "mitfahrer"] as Array<keyof ExpenseEntry>)
        .forEach((k) => fix(k, normalizeKmValue));
      fix("datum", (v) => formatDateInput(v, expense?.month?.slice(0, 4)));
    } else {
      const year = expense?.month?.slice(0, 4);
      fix("datum", (v) => formatDateInput(v, year));
      fix("abfahrt", formatTimeInput);
      fix("rueckkehr", formatTimeInput);
    }
    return out;
  };

  // ALLE Zeilen unkomprimiert senden (kein Wegfiltern leerer Zwischenzeilen):
  // der Server merged per Index — Index = Grid-Zeile. Komprimieren würde
  // Korrekturen der falschen Zelle zuordnen. Server-controlled Schlüssel
  // (meta, highlight_review, …) ignoriert der Server ohnehin (Whitelist).
  const serialized = () => ({
    ...(expense?.data ?? {}),
    entries: entries.map(canonicalizeEntry),
  });

  const stageEmployee = (employeeId: string) => {
    setPendingEmployeeId(employeeId);
    setDirty(true);
  };

  const save = async () => {
    if (!expense) return;
    setBusy(true);
    setErr(null);
    try {
      const updated = await patchExpense(expense.id, {
        data: serialized(),
        employee_id: pendingEmployeeId || null,
      });
      setExpense(updated);
      setPendingEmployeeId(updated.employee_id ?? "");
      setEntries(padEntries(updated.data.entries ?? []));
      setOriginalEntries(updated.data.entries ?? []);
      setDirty(false);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onBack = () => {
    if (dirty && !window.confirm("Ungespeicherte Änderungen. Wirklich zurück? Änderungen gehen verloren.")) return;
    closePage();
  };

  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  // Human Quality Gate (glassbox-pipeline-pattern.md §7b): ohne Freigabe der
  // Markierungen gibt es KEIN Vergleichen — auch nicht via localStorage-Restore
  // von einer anderen (freigegebenen) Karte.
  useEffect(() => {
    if (!expense) return;
    const approved = expense.data.highlight_review?.status === "approved";
    if (!approved && viewMode === "compare") setViewMode("overview");
  }, [expense, viewMode]);

  const validate = async () => {
    if (!expense) return;
    if (isFahrtenbuch && !highlightReview) {
      setGateNotice(
        "Magst du vor dem Validieren kurz die Markierung prüfen? Liegen die Rahmen auf den "
        + "richtigen km-Zeilen/Spalten? Ein Klick auf „Markierung stimmt“ oder „Markierung falsch“ "
        + "(oben im gelben Feld) genügt — dein Urteil hilft uns, die Erkennung zu verbessern.");
      gateBannerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (dirty) await save();
    setBusy(true);
    try {
      await validateExpense(expense.id);
      // UX-Angleich (Thomas 24.07.): Ansicht bleibt offen, Status wechselt
      // dynamisch weiter auf 'berechnet' (CardDetailActions-Polling).
      await refreshExpense();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  /** Eine unklare Seite zu einer Karte des gewählten Typs machen.
   *
   *  EIN Weg für alle Arten (Thomas 14.08.): Der Typ wird MITGESCHICKT, damit
   *  der Server nicht raten muss — die Ableitung aus der Registry funktioniert
   *  nur bei genau einer Art je Route. `docType` fehlt einzig beim
   *  Bestandsmandanten, der keine Registry hat; dort bleibt der Aufruf
   *  byte-gleich zu vorher.
   *
   *  Die Karte wandert auf die Route ihres Typs und wird dort neu gelesen;
   *  landet sie auf der timesheet-Route, springen wir mit. */
  const deklariereAls = async (
    route: "timesheet" | "expense",
    label: string,
    docType?: string,
  ) => {
    if (!expense) return;
    if (!confirm(`Diese Seite als ${label} verarbeiten?`)) return;
    setBusy(true);
    try {
      const r = await fetch(
        new URL(`api/expenses/${expense.id}/reclassify`, document.baseURI).toString(),
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(docType ? { target: route, doc_type: docType } : { target: route }),
        },
      );
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      const body = await r.json().catch(() => ({}));
      if (body.timesheet_id) nav(`/timesheets/${body.timesheet_id}`);
      else closePage();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!expense) return;
    if (!confirm("Diese Karte archivieren? (Original-PDF bleibt erhalten)")) return;
    setBusy(true);
    try {
      await archiveExpense(expense.id);
      closePage();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (err && !expense) return <div style={{ padding: 24 }}>Fehler: {err}</div>;
  if (!expense) return <div style={{ padding: 24 }}>Lade…</div>;

  // T13: Excel-Import-Karte (kind='excel_import', Migration 041) — kommt aus
  // einer .xlsx, pdf_path ist LEER: kein PDF-Panel, kein Formular-Grid,
  // stattdessen volle Breite Summen-/Roh-Tabellen (ExcelImportDetail).
  // #466: registry-getrieben (docType→View). `excel_import` ist das Ingestion-
  // Kind (Excel statt PDF) und gewinnt in der Registry vor dem doc_type.
  // ⚠ EINBETTUNGS-MUSTER (docs/konzept/produkt/40-detail-review.md REV-19): FRÜHER RETURN vor
  // dem Edit-Rahmen unten (READONLY_STATES/save) → read-only per Konstruktion. Editierbare
  // neue Doc-Typen (z.B. Fahrtenbuch #521) gehören IN den Rahmen (Muster 1), nicht hierher.
  if (expenseViewFor(expense) === "excel_import") {
    const excelInner = (
      <ExcelImportDetail
        cardId={expense.id}
        month={expense.month}
        state={expense.state}
        clientId={expense.client_id}
        data={expense.data}
        employees={employees}
        employeeId={expense.employee_id}
        doppelLohnartRegeln={doppelLohnartRegeln}
        modal={modal}
        onBack={onBack}
        onRefresh={async () => {
          // Karte nach einer CardDetailActions-State-Aktion neu laden →
          // ExcelImportDetail rendert mit frischem state/data neu.
          const fresh = await fetchExpense(expense.id);
          setExpense(fresh);
          setPendingEmployeeId(fresh.employee_id ?? "");
          setEntries(padEntries(fresh.data.entries ?? []));
          setOriginalEntries(fresh.data.entries ?? []);
          setDirty(false);
        }}
      />
    );
    if (modal) {
      return (
        <DetailModal dirty={false} onClose={closePage}>
          {excelInner}
        </DetailModal>
      );
    }
    return excelInner;
  }

  const suggestions = expense.data.header?.suggestions ?? [];
  const hasOcrError = !!expense.data.ocr_error;
  // Read-only ab Berechnung — Parität zur Stundenliste (Server-Gate: PATCH
  // lehnt mit 409 card_read_only ab; die UI muss konsistent sperren).
  const READONLY_STATES = new Set(["calculated", "exported", "archived", "superseded"]);
  const readOnly = READONLY_STATES.has(expense.state);
  const lockedForEdit = busy || readOnly;
  // #469: doc_type entscheidet IN-FRAME (REV-19 Muster 1) über Spaltenset,
  // Titel und Plausibilität — der Edit-Rahmen (entries/setCell/save/Gate) bleibt
  // GETEILT. `fahrtenbuch` → km-Zeilen-View, sonst das Diäten-3-Feld-Formular.
  const docType = cardDocType(expense.data);
  const isFahrtenbuch = docType === "fahrtenbuch";
  const activeFields = isFahrtenbuch ? FAHRTENBUCH_FIELDS : FIELDS;
  // Reopen/Export/Revert kommen aus dem Base-Template (CardDetailActions).
  // Plausibilität (wie Stundenliste): ungültige Autocompletion-Felder
  // (Diäten: Datum/Abfahrt/Rückkehr — Fahrtenbuch: km-Konsistenz) → rot
  // markiert + Validieren gesperrt.
  const plausIssues = expenseIssues(entries as any, expense.month?.slice(0, 4), docType);
  const plausRedCount = Object.keys(plausIssues).length;
  // #469: Layer-B-Cross-Check-Snapshot (Σ Zeilen vs. aufgedruckte km) fürs Banner.
  const crosscheck = isFahrtenbuch ? fahrtenbuchCrosscheck(expense.data) : null;
  const fahrtSummen = isFahrtenbuch
    ? ((expense.data as any)?.summen as { summe_kilometer?: number; summe_kilometer_mitfahrer?: number } | undefined)
    : undefined;
  // 2026-08-12: Der Lohnwert ist die Σ der gefahrenen Kilometer aus den ZEILEN.
  // Sie wird aus dem AKTUELLEN entries-State abgeleitet — korrigiert der
  // Operator eine Zeile, steht die neue Summe sofort da (kein OCR-Snapshot).
  // Die aufgedruckte Summe bleibt daneben als Vergleichswert stehen.
  const kmZeilen = isFahrtenbuch ? fahrtenbuchKmSummen(entries as any) : null;
  // Karte 592: Fahrtenbuch-EIGENE Overlay-Geometrie — die OCR speichert ihre
  // GEMESSENEN Lese-Fenster (meta.fahrtenbuch_geom.row_bands_pct, Zeilen aus
  // der Tinte der Datum-Spalte) + Spalten-Fenster. Markierung == Lese-Fenster
  // (Glass-Box). entries[i]._zeile mappt Tabellen-Zeile → Fenster-Index.
  // Ohne Geometrie (Alt-Karte vor Requeue): KEINE Rahmen statt falscher
  // Diäten-Rahmen (das alte deferred-TODO #469-followup ist damit erledigt).
  const fbGeom = isFahrtenbuch ? ((expense.data as any)?.meta?.fahrtenbuch_geom ?? null) : null;
  const fbBboxForCell = fbGeom && Array.isArray(fbGeom.row_bands_pct) && fbGeom.row_bands_pct.length > 0
    ? (row: number, field: string) => {
        const zeile = (entries[row] as any)?._zeile ?? row;
        const band = fbGeom.row_bands_pct[zeile];
        const xr = fbGeom.spalten_x_pct?.[field];
        if (!Array.isArray(band) || !Array.isArray(xr)) return null;
        return {
          topPct: band[0] * 100, leftPct: xr[0] * 100,
          widthPct: (xr[1] - xr[0]) * 100, heightPct: (band[1] - band[0]) * 100,
        };
      }
    : null;
  const FB_OVERLAY_FIELDS = ["datum", "km_abfahrt", "km_ankunft", "gefahrene_km", "mitfahrer", "fahrtstrecke", "zweck"];
  // Räumliches Modell: wenn meta.spatial vorhanden → pixelgenaue Geometrie.
  // Die Preview wird dann im AUFGERICHTETEN Frame geladen (rotate=…), damit
  // Overlay-/Crop-Prozente und Bild denselben Frame haben. Ohne spatial:
  // rotate=0 + statische Heuristik (bisheriges Verhalten).
  const spatial = spatialFromData(expense.data);
  const previewUrl = new URL(
    `api/cards/expense/${expense.id}/preview?rotate=${leseFrameDrehung(expense.data)}&dpi=150`,
    document.baseURI,
  ).toString();
  const highlightReview = expense.data.highlight_review ?? null;
  const highlightApproved = highlightReview?.status === "approved";
  // Karte nach State-Aktionen der CardDetailActions neu laden.
  const refreshExpense = async () => {
    const fresh = await fetchExpense(expense.id);
    setExpense(fresh);
    setPendingEmployeeId(fresh.employee_id ?? "");
    setEntries(padEntries(fresh.data.entries ?? []));
    setOriginalEntries(fresh.data.entries ?? []);
    setDirty(false);
  };

  // #506 (alle 4 Typen, PM-Status 23.07.): die zwei menschlichen Ausgänge aus
  // pending_review — Spiegel der Stundenliste, hier für Diäten + Fahrtenbuch.
  // Erfolg lädt die Karte IN PLACE frisch (Anzeige wechselt sofort, Button
  // verschwindet → kein Folgeklick-409; der auf der Stundenliste gefixte
  // Anzeige-Bug kann hier gar nicht erst entstehen).
  const reOcr = async () => {
    setBusy(true); setErr(null);
    try {
      await startExpenseOcr(expense.id);
      await refreshExpense();
      // Ende der Lesung abwarten, dann final aktualisieren (Anlassfall 23.07.).
      await waitForCardRead("expense", expense.id);
      await refreshExpense();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const approveReview = async () => {
    setBusy(true); setErr(null);
    try {
      await api.cards.approveReview("expense", expense.id, "geprüft, freigegeben");
      await refreshExpense();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const sendVerdict = async (status: "approved" | "blocked") => {
    setGateNotice(null); // Urteil abgegeben → Validieren-Gate erfüllt
    if (!expense) return;
    setBusy(true);
    setErr(null);
    try {
      const updated = await postHighlightReview(expense.id, status);
      // Server-Echo prüfen — HTTP 200 ohne gespeichertes Verdict ist ein
      // Silent-Fail (Anlassfall Timesheets 2026-06-09, mergeCardData).
      if (updated?.data?.highlight_review?.status !== status) {
        throw new Error("Urteil wurde NICHT gespeichert (Server-Antwort ohne highlight_review)");
      }
      setExpense(updated);
      // Sperren während man im Vergleichen steht → zurück in die Übersicht.
      if (status === "blocked") setViewModePersist("overview");
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Vererbte Detail-Basis (2026-07-13): Kopf/Body/Footer aus DetailViewShell.
  // Rein strukturell — identisches DOM. Editier-/Gate-/Compare-Logik im Body
  // bleibt unangetastet.
  const expTitle = (
    <>
      {expense.kind === "unknown" ? "Unklare Seite" : isFahrtenbuch ? "Fahrtenbuch" : "Diäten"} · {expense.month}
      {!readOnly && dirty && " · *ungespeichert*"}
      {readOnly && (
        <span style={{
          marginLeft: 12, fontSize: 13, fontWeight: 600,
          padding: "2px 8px", borderRadius: 4,
          background: "#fef3c7", color: "#92400e",
          border: "1px solid #fcd34d",
        }} title={`Karte im state=${expense.state} ist nicht editierbar. Server lehnt PATCH mit 409 ab.`}>
          🔒 Read-only ({expense.state})
        </span>
      )}
    </>
  );
  const expSource = expense.pdf_orig_filename ? (
    <>
      <strong>Quelle:</strong>{" "}
      <a
        href={new URL(`api/expenses/${expense.id}/pdf`, document.baseURI).toString()}
        target="_blank"
        rel="noreferrer"
        title="Original-PDF öffnen"
        style={{ color: "#1d4ed8" }}
      >
        {expense.pdf_orig_filename}
      </a>
      {typeof expense.pdf_bytes === "number" && (
        <span> · {(expense.pdf_bytes / 1024).toFixed(0)} KB</span>
      )}
      <span> · <CardIdChip id={expense.id} /></span>
    </>
  ) : null;
  const expMeta = (
    <>
          <label className="field-inline">
            <span>Mitarbeiter</span>
            <select
              value={pendingEmployeeId}
              onChange={(e) => stageEmployee(e.target.value)}
              disabled={lockedForEdit}
              title={readOnly ? `Karte ist read-only (state=${expense.state})` : undefined}
            >
              <option value="">— wählen —</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.last_name}, {e.first_name} ({e.dienstnehmernummer})
                </option>
              ))}
            </select>
          </label>
          <span className="ts-state">Status: <strong>{expense.state}</strong></span>
          {expense.kind !== "unknown" && !readOnly && orientFix === null && (
            <button
              type="button"
              className="link"
              disabled={busy}
              title="Scan ist falsch orientiert? Manuell drehen und neu erkennen lassen."
              onClick={() => setOrientFix(0)}
            >🔄 Orientierung falsch — manuelle Korrektur</button>
          )}
          {expense.kind !== "unknown" && (
            <div className="ts-viewmode-toggle" role="tablist" aria-label="Ansichtsmodus" style={{
              display: "flex", border: "1px solid #ccc", borderRadius: 4, overflow: "hidden",
            }}>
              <button type="button" role="tab" aria-selected={viewMode === "overview"}
                      onClick={() => setViewModePersist("overview")}
                      title="PDF + Tabelle nebeneinander — Vogelperspektive für Quick-Scan"
                      style={{
                        padding: "4px 12px", border: 0,
                        background: viewMode === "overview" ? "#2563eb" : "#f4f4f4",
                        color: viewMode === "overview" ? "#fff" : "#333", cursor: "pointer", fontSize: 13,
                      }}>Übersicht</button>
              <button type="button" role="tab" aria-selected={viewMode === "compare"}
                      onClick={() => highlightApproved && setViewModePersist("compare")}
                      disabled={!highlightApproved}
                      title={highlightApproved
                        ? "Pro Zelle: PDF-Ausschnitt + OCR-Wert nebeneinander — Detail-Vergleich"
                        : "Gesperrt — erst in der Übersicht „Markierung stimmt“ freigeben (Human Quality Gate)"}
                      style={{
                        padding: "4px 12px", border: 0, borderLeft: "1px solid #ccc",
                        background: viewMode === "compare" ? "#2563eb" : "#f4f4f4",
                        color: viewMode === "compare" ? "#fff" : highlightApproved ? "#333" : "#9ca3af",
                        cursor: highlightApproved ? "pointer" : "not-allowed", fontSize: 13,
                      }}>{highlightApproved ? "Vergleichen" : "🔒 Vergleichen"}</button>
            </div>
          )}
    </>
  );

  const inner = (
    <DetailViewShell
      modal={modal}
      onBack={onBack}
      title={expTitle}
      source={expSource}
      meta={expMeta}
    >

      {orientFix !== null && (
        <div style={{
          background: "#fffbeb", border: "1px solid #f59e0b", borderLeft: "4px solid #f59e0b",
          borderRadius: 4, padding: 14, margin: "10px 0", fontSize: 13,
        }}>
          <strong>Orientierung falsch — manuelle Korrektur</strong>
          <div style={{ margin: "6px 0", color: "#92400e" }}>
            Mit ↺ / ↻ drehen, bis das Formular richtig steht (Kopfzeile oben).
            „Speichern &amp; neu erkennen“ startet die Texterkennung mit genau dieser
            Orientierung neu — die Automatik dreht dann nicht mehr dagegen.
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
            <img
              src={new URL(`api/cards/expense/${expense.id}/preview?rotate=${orientFix}&dpi=72`, document.baseURI).toString()}
              alt={`Scan-Vorschau, ${orientFix}° gedreht`}
              style={{ maxWidth: 460, maxHeight: 360, border: "1px solid #d1d5db", borderRadius: 3, background: "#fff" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button type="button" disabled={orientBusy}
                        title="90° gegen den Uhrzeigersinn drehen"
                        onClick={() => setOrientFix(((orientFix + 270) % 360))}>↺ 90° links</button>
                <button type="button" disabled={orientBusy}
                        title="90° im Uhrzeigersinn drehen"
                        onClick={() => setOrientFix(((orientFix + 90) % 360))}>↻ 90° rechts</button>
                <span style={{ color: "#6b7280" }}>Drehung: <strong>{orientFix}°</strong></span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="primary"
                  disabled={orientBusy}
                  onClick={async () => {
                    setOrientBusy(true);
                    setErr(null);
                    try {
                      const r = await fetch(
                        new URL(`api/cards/expense/${expense.id}/resolve-orientation`, document.baseURI).toString(),
                        {
                          method: "POST",
                          credentials: "same-origin",
                          headers: { "content-type": "application/json" },
                          body: JSON.stringify({ angle: orientFix }),
                        },
                      );
                      if (!r.ok) throw new Error(humanServerError(await r.text()));
                      // Karte ist jetzt in der OCR-Queue — Detail schließen,
                      // das Board zeigt sie in der OCR-Spalte.
                      setOrientFix(null);
                      closePage();
                    } catch (e: any) {
                      setErr(e.message);
                    } finally {
                      setOrientBusy(false);
                    }
                  }}
                >{orientBusy ? "Speichere…" : "💾 Speichern & neu erkennen"}</button>
                <button type="button" disabled={orientBusy} onClick={() => setOrientFix(null)}>Abbrechen</button>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", maxWidth: 320 }}>
                Hinweis: Die aktuell erkannten Werte werden durch die neue
                Erkennung ersetzt.
              </div>
            </div>
          </div>
        </div>
      )}

      {suggestions.length > 0 && !pendingEmployeeId && (
        <div className="suggestions-row">
          <span>OCR-Vorschlag:</span>
          {suggestions.map((s) => (
            <button
              key={s.id}
              className="suggestion-chip"
              onClick={() => stageEmployee(s.id)}
              disabled={lockedForEdit}
            >
              {s.name} <span className="score">({Math.round(s.score * 100)}%)</span>
            </button>
          ))}
        </div>
      )}

      {hasOcrError && (
        <div className="warning-box">
          OCR-Fehler: <code>{expense.data.ocr_error}</code> — Werte manuell eintragen oder warten bis Engines live sind.
        </div>
      )}

      {/* WAS EINE UNKLARE SEITE ZEIGT (Thomas 14.08.2026, Definition):
       *  „im endeffekt braucht es nur die quelle angezeigt und dann eben die
       *  optionen ... aber eben die richtigen typen."
       *
       *  Also: der Scan (weiter unten) und diese Entscheidung — sonst nichts.
       *  Kein Erfassungsraster: Solange der Typ nicht feststeht, ist jedes
       *  Raster geraten. Auf einer 3G-Seite stand hier bisher das Diäten-Raster
       *  des Bestandsmandanten (Datum/Abfahrt/Rückkehr, 22 leere Zeilen) —
       *  bei einem Mandanten, der gar keine Diäten abrechnet.
       *
       *  Die Auswahl kommt aus der MANDANTEN-REGISTRY, nicht aus dem Code —
       *  dieselbe Quelle, die der Kanban schon nutzt (KanbanPage.tsx, Karte
       *  #563). Zwei Oberflächen dürfen dieselbe Frage nicht verschieden
       *  beantworten: hier fehlten 3G die eigenen Arten („Zulagenblatt",
       *  „Geleistete Stunden für Zusätzliche Projekte"), angeboten wurde
       *  stattdessen „Stundenliste" — die es bei 3G nicht gibt.
       *
       *  Jeder Knopf gibt den Typ MIT (`doc_type`). Ohne ihn leitet der Server
       *  ihn aus der Registry ab, was nur bei genau einer Art je Route geht —
       *  seit `projektstunden` (Karte 721) sind es bei 3G zwei, und der Aufruf
       *  ohne Typ endet in `doc_type_required`.
       *
       *  Ohne Registry (Bestandsmandant) bleibt es exakt beim alten Verhalten:
       *  zwei Knöpfe, alte Beschriftung, Aufruf ohne doc_type. */}
      {expense.kind === "unknown" && (
        <div className="classify-picker">
          <div className="classify-picker-title">
            Diese Seite wurde noch nicht klassifiziert — bitte entscheide
          </div>
          <div className="classify-picker-actions">
            {klassifikationsOptionen(mandantTypen, {
              timesheet: typLabel("timesheet", "Stundenliste"),
              expense: typLabel("expense", "Diäten"),
            }).map((o) => (
              <button
                key={o.docType ?? o.route}
                type="button"
                className="primary"
                disabled={busy}
                onClick={() => void deklariereAls(o.route, o.label, o.docType)}
              >Als {o.label} verarbeiten</button>
            ))}
            <button
              type="button"
              className="link"
              disabled={busy}
              onClick={archive}
            >Verwerfen</button>
          </div>
        </div>
      )}

      {/* Geführte Validator-Navigation (Base-Template, Stundenlisten-Mechanik):
       *  sichtbare Begründung + Hinspringen — in Übersicht UND Vergleichen. */}
      {expense.kind !== "unknown" && (
        <PlausibilityNav
          issues={plausIssues}
          onFocusCell={(row, field) => setActiveCell({ row, field: field as ExpenseFieldName })}
        />
      )}

      {/* Human Quality Gate (glassbox-pipeline-pattern.md §7b): das Urteil über
       *  die Markierungs-Qualität wird in der ÜBERSICHT gefällt — nur hier sieht
       *  man ALLE Overlay-Rahmen am ganzen PDF. Am kleinen Crop im Vergleichen-
       *  Modus ist ein verrutschtes Raster nicht erkennbar (silent corruption). */}
      {gateNotice && !highlightReview && (
        <div style={{
          background: "#eff6ff", border: "1px solid #2563eb", borderRadius: 4,
          padding: "8px 12px", fontSize: 13, color: "#1e3a8a", marginBottom: 8,
        }}>{gateNotice}</div>
      )}
      {expense.kind !== "unknown" && viewMode === "overview" && (
        highlightReview === null ? (
          <div ref={gateBannerRef} style={{
            background: "#fffbeb", border: "1px solid #f59e0b", borderLeft: "4px solid #f59e0b",
            padding: "10px 14px", borderRadius: 4, fontSize: 13, color: "#92400e",
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 8,
          }}>
            <div style={{ flex: "1 1 320px" }}>
              <strong>Markierung prüfen (Human Quality Gate)</strong>
              <div style={{ marginTop: 4 }}>
                Liegen die blauen Rahmen im PDF auf den richtigen Zeilen/Spalten?
                Geometrie: {spatial ? "pixelgenau (Template-Homography)" : "Heuristik (statisches Raster)"}.
                Der Vergleichen-Modus bleibt gesperrt, bis du freigibst — dein
                Urteil wird mit Geometrie-Kontext gespeichert, damit wir aus
                Markierungs-Fehlern lernen.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="primary success" disabled={busy}
                      onClick={() => sendVerdict("approved")}>
                ✓ Markierung stimmt — Vergleichen freigeben
              </button>
              <button type="button" className="primary" disabled={busy}
                      style={{ background: "#dc2626" }}
                      onClick={() => sendVerdict("blocked")}>
                ✗ Markierung falsch — sperren
              </button>
            </div>
          </div>
        ) : highlightApproved ? (
          <div style={{
            fontSize: 12, color: "#166534", background: "#f0fdf4",
            border: "1px solid #bbf7d0", borderRadius: 4, padding: "6px 10px",
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8,
          }}>
            <span>
              ✓ Markierung freigegeben durch {highlightReview?.by_name ?? "User"}
              {highlightReview?.at ? ` am ${new Date(highlightReview.at).toLocaleString("de-AT")}` : ""}
              {" "}— Vergleichen ist verifiziert
            </span>
            <button type="button" className="link" disabled={busy}
                    title="Markierung passt doch nicht — Vergleichen sperren (wird gespeichert)"
                    onClick={() => sendVerdict("blocked")}>
              Sperren
            </button>
          </div>
        ) : (
          <div style={{
            fontSize: 12, color: "#7f1d1d", background: "#fef2f2",
            border: "1px solid #fecaca", borderRadius: 4, padding: "6px 10px",
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8,
          }}>
            <span>
              ❌ Markierung gesperrt durch {highlightReview?.by_name ?? "User"}
              {highlightReview?.at ? ` am ${new Date(highlightReview.at).toLocaleString("de-AT")}` : ""}
              {" "}— Vergleichen deaktiviert (gespeichert; Re-OCR stellt das Gate neu)
            </span>
            <button type="button" className="link" disabled={busy}
                    onClick={() => sendVerdict("approved")}>
              Doch freigeben
            </button>
          </div>
        )
      )}

      {/* Vergleichen-Body: wie bei der Stundenliste warm gemountet (display-
       *  toggle) ab erster Aktivierung — Preview + Crops bleiben im Cache. */}
      {compareEverActivated && expense.kind !== "unknown" && (
        <div className="ts-compare-body" style={{ display: viewMode === "compare" ? "block" : "none" }}>
          <ExpenseCompareView
            previewUrl={previewUrl}
            entries={entries as any}
            originals={originalEntries as any}
            fields={FIELDS as any}
            spatial={spatial}
            review={highlightReview}
            cardYear={expense.month?.slice(0, 4)}
            busy={lockedForEdit}
            onVerdict={sendVerdict}
            setCell={(row, key, value) => setCell(row, key as keyof ExpenseEntry, value)}
            activeCell={activeCell}
            onCellHover={setActiveCell}
          />
        </div>
      )}
      <div
        className="ts-detail-body"
        style={{
          position: "relative",
          ...(viewMode === "compare" && expense.kind !== "unknown" ? { display: "none" } : {}),
        }}
      >
        {!previewReady && (
          <div className="page-loading-curtain" role="status" aria-live="polite">
            <div className="spinner" />
            <div className="label">📄 Seite wird geladen …</div>
            <div className="sub">Bitte warten — die Markierungen werden auf das PDF ausgerichtet. Noch nichts eintragen.</div>
          </div>
        )}
        {/* Karte 592 (löst TODO #469-followup): Fahrtenbuch nutzt seine EIGENE
            gemessene Geometrie (fbBboxForCell aus meta.fahrtenbuch_geom);
            Alt-Karten ohne Geometrie zeigen KEINE Rahmen (ehrlich) statt der
            falschen Diäten-Rahmen. Diäten unverändert (spatial/Heuristik). */}
        <ExpensePdfHighlights
          imageUrl={previewUrl}
          pdfHrefForOpen={new URL(`api/expenses/${expense.id}/pdf`, document.baseURI).toString()}
          rowCount={entries.length}
          fields={isFahrtenbuch ? (fbBboxForCell ? FB_OVERLAY_FIELDS : []) : OVERLAY_FIELDS}
          activeCell={activeCell}
          onCellHover={setActiveCell}
          spatial={isFahrtenbuch ? null : spatial}
          bboxForCell={fbBboxForCell ?? undefined}
          onReady={() => setPreviewReady(true)}
        />
        {/* Erfassungsraster NUR fuer eine Karte, deren Typ feststeht (Thomas
            14.08.2026). Eine unklare Seite zeigt die QUELLE und die Entscheidung —
            sonst nichts. Vorher rendete hier das Diaeten-Raster (Datum/Abfahrt/
            Rueckkehr, 22 leere Zeilen) auch auf Seiten eines Mandanten, der gar
            keine Diaeten fuehrt: ein Formular als Platzhalter fuer eine Frage,
            die noch offen ist. */}
        {expense.kind !== "unknown" && (
          <div className="ts-data">
            <table className="ts-table" ref={tableRef}>
              <thead>
                <tr>
                  {activeFields.map((f) => (<th key={f.key}>{f.label}</th>))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((row, idx) => (
                  <tr key={idx}>
                    {activeFields.map((f) => {
                      const bucket = bucketOf(cellConf(row[f.key]));
                      const current = cellVal(row[f.key]);
                      const isActive = activeCell?.row === idx && activeCell?.field === f.key;
                      const issue = plausIssues[`${idx}.${f.key}`];
                      // Excel-artige Pfeiltasten-Navigation — übernommen von der
                      // Stundenliste (TimesheetDetailPage): Up/Down = Zeile,
                      // Enter = nächste Zeile, Left/Right springen nur, wenn der
                      // Cursor am Feldrand steht (Text-Cursor-Bewegung bleibt).
                      const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (!tableRef.current) return;
                        const all = Array.from(
                          tableRef.current.querySelectorAll<HTMLInputElement>("input.cell-input"),
                        );
                        const inputIdx = all.indexOf(e.currentTarget);
                        if (inputIdx < 0) return;
                        const cols = activeFields.length;
                        const focusAt = (n: number) => {
                          const t = all[n];
                          if (t) { t.focus(); t.select(); }
                        };
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          focusAt(inputIdx - cols);
                          return;
                        }
                        if (e.key === "ArrowDown" || e.key === "Enter") {
                          e.preventDefault();
                          focusAt(inputIdx + cols);
                          return;
                        }
                        if (e.key === "ArrowLeft") {
                          const inp = e.currentTarget;
                          if (inp.selectionStart === 0 && inp.selectionEnd === 0) {
                            e.preventDefault();
                            focusAt(inputIdx - 1);
                          }
                          return;
                        }
                        if (e.key === "ArrowRight") {
                          const inp = e.currentTarget;
                          const end = inp.value.length;
                          if (inp.selectionStart === end && inp.selectionEnd === end) {
                            e.preventDefault();
                            focusAt(inputIdx + 1);
                          }
                          return;
                        }
                      };
                      return (
                        <td
                          key={f.key}
                          className={`conf-${bucket}`}
                          data-row={idx}
                          data-field={f.key}
                          onPointerEnter={() => setActiveCell({ row: idx, field: f.key as ExpenseFieldName })}
                          onPointerLeave={() => setActiveCell(null)}
                          style={{
                            ...(isActive ? { outline: "2px solid #2563eb", outlineOffset: -2 } : {}),
                            ...(issue ? { background: "rgba(220, 38, 38, 0.08)" } : {}),
                          }}
                          title={issue}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <input
                            type="text"
                            className="cell-input"
                            value={current}
                            placeholder={f.placeholder}
                            onChange={(e) => setCell(idx, f.key, e.target.value)}
                            onKeyDown={onKeyDown}
                            onFocus={(e) => {
                              // Klick überschreibt direkt (wie Stundenliste) —
                              // der nächste Tastendruck ersetzt den Wert.
                              e.currentTarget.select();
                              setActiveCell({ row: idx, field: f.key as ExpenseFieldName });
                            }}
                            style={issue ? { border: "2px solid #dc2626", background: "#fef2f2" } : undefined}
                            onBlur={() => {
                              // #469: Fahrtenbuch normalisiert die km-Ganzzahlen
                              // ("12.345"→"12345"); datum/Freitext bleiben roh.
                              if (isFahrtenbuch) {
                                if (f.key === "km_abfahrt" || f.key === "km_ankunft"
                                    || f.key === "gefahrene_km" || f.key === "mitfahrer") {
                                  const formatted = normalizeKmValue(current);
                                  if (formatted !== current) setCell(idx, f.key, formatted);
                                } else if (f.key === "datum") {
                                  // 592-R3 (Thomas): dieselbe Datums-Usability wie
                                  // Diäten — "020326"→"02.03.2026", "2.3"→Jahr der
                                  // Karte. Das Datum ist seit R2 Pflichtfeld; ohne
                                  // Autoformat müsste die Kundin hier von Hand tippen.
                                  const formatted = formatDateInput(current, expense.month?.slice(0, 4));
                                  if (formatted !== current) setCell(idx, f.key, formatted);
                                }
                                return;
                              }
                              // Diäten: Zeit-/Datums-Autoformat wie Stundenliste
                              // ("8"→"08:00", "160326"→"16.03.2026"). Korrekturen
                              // trackt der Server beim PATCH (mergeExpenseData).
                              if (f.key === "abfahrt" || f.key === "rueckkehr") {
                                const formatted = formatTimeInput(current);
                                if (formatted !== current) setCell(idx, f.key, formatted);
                              } else if (f.key === "datum") {
                                const formatted = formatDateInput(current, expense.month?.slice(0, 4));
                                if (formatted !== current) setCell(idx, f.key, formatted);
                              }
                            }}
                            disabled={lockedForEdit}
                            title={readOnly ? `Karte ist read-only (state=${expense.state})` : undefined}
                          />
                          {issue && <PlausibilityBadge reason={issue} />}
                          {/* Datum-Vererbung „wie oben“ (Excel-artig): nur Diäten
                              (das Fahrtenbuch-Datum DD.MM.JJ folgt anderer Logik) und
                              nur wenn das Feld leer ist ODER der Validator anschlagt —
                              gültige Eingaben werden nie überschrieben. */}
                          {f.key === "datum" && !isFahrtenbuch && !lockedForEdit
                            && canInheritDate(entries as any, idx, expense.month?.slice(0, 4))
                            && (() => {
                              const inh = inheritableDate(entries as any, idx, expense.month?.slice(0, 4));
                              if (!inh) return null;
                              return (
                                <button
                                  type="button"
                                  className="link"
                                  style={{ opacity: 0.55, fontSize: 12, whiteSpace: "nowrap" }}
                                  title={`Datum wie oben übernehmen (Zeile ${inh.fromRow + 1}: ${inh.value})`}
                                  onClick={() => setCell(idx, "datum", inh.value)}
                                >⤓ wie oben</button>
                              );
                            })()}
                          </div>
                        </td>
                      );
                    })}
                    <td>
                      {/* Fixes Grid: Zeile kann nur GELEERT werden, nie gelöscht. */}
                      <button
                        className="link"
                        onClick={() => clearRow(idx)}
                        disabled={lockedForEdit || !activeFields.some((f) => cellVal(row[f.key]).trim().length > 0)}
                        title="Zeile leeren (das Grid ist fix — Zeilen können nicht gelöscht werden)"
                      >⌫</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
              Fixes Formular-Grid: immer {EXPENSE_FIXED_ROWS} Zeilen — leere Zeilen bleiben leer,
              Zeilen können nicht hinzugefügt oder gelöscht werden.
            </div>
          </div>
        )}
      </div>

      {/* #469 Fahrtenbuch-Auswertung (Modell: Zulagenblatt-Summen/Cross-Check).
        * Layer A = die aufgedruckten Monats-Summen (der LOHNWERT); Layer B =
        * Σ-Cross-Check der gelesenen km-Zeilen gegen die aufgedruckte km-Summe
        * (Audit-Netz, ocr/fahrtenbuch.py). Der Cross-Check-Snapshot stammt vom
        * Worker (meta.layer_b_crosscheck) — grün bei match, rot mit Differenz sonst. */}
      {isFahrtenbuch && (
        <div className="calc-panel">
          <h3>Fahrtenbuch · Kilometer</h3>
          {crosscheck && (
            crosscheck.match ? (
              <div style={{
                fontSize: 13, color: "#166534", background: "#f0fdf4",
                border: "1px solid #bbf7d0", borderRadius: 4, padding: "8px 12px",
                display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10,
              }}>
                ✓ Cross-Check ok — Summe der Zeilen (<strong>{crosscheck.zeilenSummeKm ?? "–"} km</strong>)
                {" "}stimmt mit der aufgedruckten Summe (<strong>{crosscheck.aufgedrucktKm ?? "–"} km</strong>) überein
                {typeof crosscheck.nZeilen === "number" ? ` · ${crosscheck.nZeilen} Zeilen` : ""}.
              </div>
            ) : (
              <div style={{
                fontSize: 13, color: "#7f1d1d", background: "#fef2f2",
                border: "1px solid #fecaca", borderLeft: "4px solid #dc2626", borderRadius: 4,
                padding: "8px 12px", marginBottom: 10,
              }}>
                <strong>⚠ Abweichung zur aufgedruckten Summe</strong> — die Summe der km-Zeilen
                (<strong>{kmZeilen?.gefahren ?? crosscheck.zeilenSummeKm ?? "–"} km</strong>) weicht von der
                aufgedruckten Summe Kilometer (<strong>{crosscheck.aufgedrucktKm ?? "–"} km</strong>) ab.
                <div style={{ marginTop: 4, color: "#b91c1c" }}>
                  Gerechnet wird mit der Summe der Zeilen — sie ist der Wert für die Abrechnung.
                  Die aufgedruckte Summe dient dem Vergleich: prüfe am Scan, ob eine Fahrt fehlt
                  oder eine Zeile falsch gelesen wurde. Korrigierst du eine Zeile, passt sich die
                  Summe sofort an.
                </div>
              </div>
            )
          )}
          <dl className="kv-grid">
            <dt>Summe Kilometer</dt>
            <dd>
              <strong>{kmZeilen ? `${kmZeilen.gefahren} km` : "—"}</strong>
              <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                aus {kmZeilen?.zeilen ?? 0} Zeilen gerechnet
                {typeof fahrtSummen?.summe_kilometer === "number"
                  ? ` · laut Blatt ${fahrtSummen.summe_kilometer} km`
                  : ""}
              </span>
            </dd>
            <dt>Summe Kilometer (Mitfahrer)</dt>
            <dd>
              {kmZeilen ? `${kmZeilen.mitfahrer} km` : "—"}
              {typeof fahrtSummen?.summe_kilometer_mitfahrer === "number" && (
                <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                  laut Blatt {fahrtSummen.summe_kilometer_mitfahrer} km
                </span>
              )}
            </dd>
          </dl>
          {/* Ein km-Wert, der nicht als Zahl lesbar ist, fehlt in der Summe —
              das darf nicht still passieren (der Worker routet solche Karten
              zusaetzlich nach „Pruefen"). */}
          {kmZeilen && kmZeilen.unlesbar > 0 && (
            <div style={{
              fontSize: 13, color: "#7f1d1d", background: "#fef2f2",
              border: "1px solid #fecaca", borderRadius: 4, padding: "8px 12px", marginTop: 8,
            }}>
              <strong>{kmZeilen.unlesbar}</strong>{" "}
              {kmZeilen.unlesbar === 1 ? "km-Wert ist" : "km-Werte sind"} nicht als Zahl lesbar und
              {" "}deshalb <strong>nicht</strong> in der Summe enthalten — bitte die rot markierten
              Zeilen korrigieren.
            </div>
          )}
        </div>
      )}

      {/* Diäten-Stunden-Panel (2026-06-10, Thomas): nach „Validieren" rechnet
        * der Worker pro Reisetag Rückkehr − Abfahrt und rundet auf ganze
        * Stunden AUF (jede angefangene Stunde = ganze Stunde, 8,5 h → 9 h).
        * Die Monatssumme geht so in den CSV-Export (MAID + Name + Stunden).
        * Das alte Tagessatz-Panel bleibt deaktiviert (SHOW_EXPENSE_CALC_PANEL). */}
      {(expense.data as any)?.calculation?.kind === "diaeten_stunden_v1" && (
        <div className="calc-panel">
          <h3>Diäten-Stunden</h3>
          <dl className="kv-grid">
            <dt>Summe (Monat)</dt>
            <dd><strong>{(expense.data as any).calculation.totals.hours_total} Stunden</strong></dd>
            <dt>Gewertete Reisetage</dt>
            <dd>{(expense.data as any).calculation.totals.days_counted}</dd>
            <dt>Regel</dt>
            <dd style={{ color: "#6b7280" }}>{(expense.data as any).calculation.rule}</dd>
          </dl>
          {Array.isArray((expense.data as any).calculation.days)
            && (expense.data as any).calculation.days.length > 0 && (
            <table className="ts-table" style={{ marginTop: 8, fontSize: 12 }}>
              <thead>
                <tr><th>Datum</th><th>Abfahrt</th><th>Rückkehr</th><th>Dauer</th><th>Gerundet</th></tr>
              </thead>
              <tbody>
                {(expense.data as any).calculation.days.map((d: any, i: number) => (
                  <tr key={i}>
                    <td>{d.datum}</td>
                    <td>{d.abfahrt}</td>
                    <td>{d.rueckkehr}</td>
                    <td>{String(d.hours_exact).replace(".", ",")} h</td>
                    <td><strong>{d.hours_rounded} h</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {SHOW_EXPENSE_CALC_PANEL && (expense.data as any)?.calculation?.totals?.tagessatz_total != null && (
        <div className="calc-panel">
          <h3>Diäten-Berechnung</h3>
          <dl className="kv-grid">
            <dt>Gesamt-Tagessatz</dt>
            <dd>€ {(expense.data as any).calculation.totals.tagessatz_total.toFixed(2)}</dd>
            <dt>Inland / Ausland / Halbtage</dt>
            <dd>
              {(expense.data as any).calculation.totals.inland_days} / {" "}
              {(expense.data as any).calculation.totals.ausland_days} / {" "}
              {(expense.data as any).calculation.totals.halftime_days}
            </dd>
          </dl>
        </div>
      )}

      {/* Base-Template: state-getriebene Standard-Aktionen (Reopen/Export/
        * Re-Export/Revert) kommen aus CardDetailActions — identisch mit der
        * Stundenliste. Kind-spezifisch bleiben Archivieren/Speichern/Validieren. */}
      <CardDetailActions
        kind="expense"
        cardId={expense.id}
        month={expense.month}
        clientId={expense.client_id}
        state={expense.state}
        busy={busy}
        err={err}
        setBusy={setBusy}
        setErr={setErr}
        onRefresh={refreshExpense}
        hideReclassify={expense.kind === "unknown"}
      >
        <button className="link" onClick={archive} disabled={busy}>Archivieren</button>
        {!readOnly && (
          <>
            <button type="button" className="primary" onClick={save} disabled={!dirty || lockedForEdit}>
              Speichern
            </button>
            {expense.state === "pending_review" ? (
              <>
                {/* #506 (alle 4 Typen): „Prüfen"-Karte hat zwei menschliche Ausgänge —
                    Freigabe ODER Re-OCR (statt Validieren, das aus pending_review 409 gäbe).
                    Spiegel der Stundenlisten-Aktionsleiste. */}
                <button
                  type="button"
                  className="primary success"
                  onClick={approveReview}
                  disabled={busy}
                  title="Geprüft und in Ordnung → weiter zu OCR fertig (danach validierbar). Die Werte bleiben unverändert."
                >
                  ✓ Geprüft → freigeben
                </button>
                <button
                  type="button"
                  className="link"
                  onClick={reOcr}
                  disabled={busy}
                  title="Neu einlesen lassen (bestehende Korrekturen bleiben erhalten)."
                >
                  ↻ Neu lesen lassen
                </button>
              </>
            ) : (
              <button
                type="button"
                className="primary success"
                onClick={validate}
                disabled={lockedForEdit || !pendingEmployeeId || plausRedCount > 0}
                title={
                  plausRedCount > 0
                    ? `${plausRedCount} Plausibilitäts-Fehler müssen zuerst korrigiert werden (rote Markierungen: ungültiges Datum/Uhrzeit)`
                    : !pendingEmployeeId
                      ? "Zuerst Mitarbeiter zuordnen"
                      : ""
                }
              >
                Validieren
              </button>
            )}
          </>
        )}
      </CardDetailActions>
    </DetailViewShell>
  );

  if (modal) {
    return (
      <DetailModal dirty={dirty} onClose={closePage}>
        {inner}
      </DetailModal>
    );
  }
  return inner;
}
